import React, { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Clock, BookOpen, Lock, Star, Headphones, Download, Rss, Copy, Check, Mail } from "lucide-react";
import { getSeriesBySlug, getChaptersForSeries } from "../data/series";
import { useAuth } from "../AuthContext";
import { ScheduleModal } from "../components/DailyDelivery";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SeriesPage = () => {
  const { seriesSlug } = useParams();
  const { user, isPatron, token } = useAuth();
  const series = getSeriesBySlug(seriesSlug);
  const chapters = getChaptersForSeries(seriesSlug);
  const [audioStatus, setAudioStatus] = useState(null);
  const [rssCopied, setRssCopied] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  useEffect(() => {
    const fetchAudio = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/audio/status/${seriesSlug}`);
        setAudioStatus(res.data);
      } catch { /* ignore */ }
    };
    if (seriesSlug) fetchAudio();
  }, [seriesSlug]);

  if (!series) return <Navigate to="/" replace />;

  const isPatronGated = series.tier === "patron";
  const hasAudio = audioStatus && audioStatus.generated_count > 0;

  const rssUrl = isPatronGated && token
    ? `${BACKEND_URL}/api/rss/${seriesSlug}?token=${token}`
    : `${BACKEND_URL}/api/rss/${seriesSlug}`;

  const canAccessRss = !isPatronGated || isPatron;

  const podcastDeepLink = `podcast://${rssUrl.replace(/^https?:\/\//, "")}`;

  const handleCopyRss = () => {
    navigator.clipboard.writeText(rssUrl);
    setRssCopied(true);
    setTimeout(() => setRssCopied(false), 2000);
  };

  return (
    <div data-testid="series-page" className="min-h-screen bg-ff-bg">
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24" style={{ backgroundColor: `${series.accentColor}08` }}>
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              {isPatronGated && (
                <div data-testid="patron-badge" className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 text-xs font-semibold tracking-wider uppercase rounded-full" style={{ background: '#c8860a22', color: '#c8860a', border: '1px solid #c8860a44' }}>
                  <Star size={12} fill="currentColor" />
                  Patron Exclusive
                </div>
              )}

              <p className="label mb-4" style={{ color: series.accentColor }}>{series.tag}</p>
              <h1 data-testid="series-title" className="font-serif text-display text-ff-text mb-6">{series.title}</h1>
              <p className="text-ff-text-muted text-sm mb-6">by <span className="text-ff-text">{series.author}</span></p>
              <p className="text-ff-text-muted font-light leading-relaxed mb-8">{series.description}</p>

              {/* Series Meta */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-ff-text-muted text-sm">
                  <BookOpen size={16} style={{ color: series.accentColor }} />
                  {series.chapterCount} Chapters
                </div>
                <div className="flex items-center gap-2 text-ff-text-muted text-sm">
                  <Clock size={16} style={{ color: series.accentColor }} />
                  {series.publishDay}
                </div>
                {hasAudio && (
                  <div className="flex items-center gap-2 text-ff-text-muted text-sm">
                    <Headphones size={16} style={{ color: series.accentColor }} />
                    Audio Available
                  </div>
                )}
                <span className="px-3 py-1 text-xs font-semibold rounded-full" style={{ backgroundColor: `${series.accentColor}20`, color: series.accentColor }}>
                  {series.status}
                </span>
              </div>

              {isPatronGated && !isPatron ? (
                <Link to="/subscribe" data-testid="patron-subscribe-btn" className="btn-primary inline-flex items-center gap-2" style={{ backgroundColor: series.accentColor }}>
                  <Lock size={16} />
                  Become a Patron to Read
                </Link>
              ) : (
                <div className="flex flex-wrap gap-3">
                  <Link to={`/${seriesSlug}/chapter-1`} data-testid="start-reading-btn" className="btn-primary inline-flex items-center gap-2" style={{ backgroundColor: series.accentColor }}>
                    Start from Chapter 1
                    <ChevronRight size={18} />
                  </Link>
                  <button
                    onClick={() => setShowSchedule(true)}
                    data-testid="schedule-daily-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border text-sm font-medium transition-all hover:bg-ff-surface"
                    style={{ borderColor: series.accentColor, color: series.accentColor }}
                  >
                    <Mail size={16} />
                    Daily to Inbox
                  </button>
                  {user && (
                    <Link to={`/${seriesSlug}/chapter-1`} data-testid="start-listening-btn" className="inline-flex items-center gap-2 px-5 py-2.5 border text-sm font-medium transition-all hover:bg-ff-surface" style={{ borderColor: series.accentColor, color: series.accentColor }}>
                      <Headphones size={16} />
                      Listen
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <div className="aspect-square rounded-lg overflow-hidden border border-ff-border" style={{ boxShadow: `0 20px 60px ${series.accentColor}30` }}>
                <img src={series.coverImage} alt={series.title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audio / Download / RSS Bar */}
      {(user || canAccessRss) && (
        <section data-testid="series-media-bar" className="py-6 bg-ff-surface border-y border-ff-border">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-6">
              {user && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${series.accentColor}20` }}>
                    <Headphones size={16} style={{ color: series.accentColor }} />
                  </div>
                  <div>
                    <p className="text-ff-text text-sm font-medium">Listen to this series</p>
                    <p className="text-ff-text-muted text-xs">Audio narration available on each chapter</p>
                  </div>
                </div>
              )}

              {isPatron && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${series.accentColor}20` }}>
                    <Download size={16} style={{ color: series.accentColor }} />
                  </div>
                  <div>
                    <p className="text-ff-text text-sm font-medium">Download PDFs</p>
                    <p className="text-ff-text-muted text-xs">Individual chapters or the full series from any chapter page</p>
                  </div>
                </div>
              )}

              {canAccessRss && (
                <div className="flex items-center gap-2 text-sm ml-auto">
                  <a
                    href={podcastDeepLink}
                    data-testid="apple-podcasts-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 text-ff-text text-sm transition-colors"
                    style={{ background: `${series.accentColor}25`, border: `1px solid ${series.accentColor}44` }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a10 10 0 0 1 10 10c0 4.6-3.1 8.4-7.3 9.6" />
                      <path d="M12 2a10 10 0 0 0-10 10c0 4.6 3.1 8.4 7.3 9.6" />
                      <path d="M12 6a6 6 0 0 1 6 6c0 2.2-1.2 4.2-3 5.2" />
                      <path d="M12 6a6 6 0 0 0-6 6c0 2.2 1.2 4.2 3 5.2" />
                      <circle cx="12" cy="12" r="2" />
                      <path d="M12 14v8" />
                    </svg>
                    Open in Apple Podcasts
                  </a>
                  <button onClick={handleCopyRss} data-testid="rss-copy-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-ff-border text-ff-text-muted hover:text-ff-text hover:border-ff-text-muted transition-colors text-sm">
                    {rssCopied ? <Check size={14} className="text-green-400" /> : <Rss size={14} />}
                    {rssCopied ? "Copied!" : "Copy RSS Feed"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Chapter List */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <h2 className="font-serif text-2xl text-ff-text mb-8">All Chapters</h2>

          <div data-testid="series-chapter-list" className="space-y-0 border-t border-ff-border">
            {chapters.map((chapter) => {
              const isLocked = isPatronGated && !isPatron;
              const ChapterWrapper = isLocked ? 'div' : Link;
              const wrapperProps = isLocked
                ? { className: "chapter-item flex items-center justify-between p-4 md:p-5 border-b border-ff-border opacity-70" }
                : {
                    to: `/${seriesSlug}/${chapter.slug}`,
                    className: "chapter-item flex items-center justify-between p-4 md:p-5 border-b border-ff-border group",
                  };

              const chapterHasAudio = audioStatus?.generated?.includes(chapter.number);

              return (
                <ChapterWrapper key={chapter.slug} data-testid={`series-chapter-${chapter.number}`} {...wrapperProps}>
                  <div className="flex items-center gap-4 md:gap-6">
                    <span className="w-10 h-10 flex items-center justify-center text-sm font-semibold rounded-full shrink-0" style={{ backgroundColor: `${series.accentColor}15`, color: series.accentColor }}>
                      {isLocked ? <Lock size={14} /> : chapter.number}
                    </span>
                    <div>
                      <h3 className="font-serif text-lg text-ff-text group-hover:text-opacity-80 transition-colors">{chapter.title}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <p className="text-ff-text-muted text-xs">Chapter {chapter.number}</p>
                        {chapterHasAudio && (
                          <span className="text-xs flex items-center gap-1" style={{ color: series.accentColor }}>
                            <Headphones size={10} /> Audio
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isLocked ? (
                    <span className="text-xs text-ff-text-muted">Patron only</span>
                  ) : (
                    <ChevronRight size={20} className="text-ff-text-muted group-hover:text-ff-text group-hover:translate-x-1 transition-all" style={{ color: series.accentColor }} />
                  )}
                </ChapterWrapper>
              );
            })}
          </div>

          {/* Patron CTA for gated series */}
          {isPatronGated && !isPatron && (
            <div className="mt-8 p-8 border border-ff-border text-center" style={{ borderColor: `${series.accentColor}33`, background: `${series.accentColor}08` }}>
              <Star size={24} className="mx-auto mb-3" style={{ color: series.accentColor }} />
              <h3 className="font-serif text-xl text-ff-text mb-2">Unlock {series.title}</h3>
              <p className="text-ff-text-muted text-sm mb-4 max-w-md mx-auto">
                Get instant access to all {series.chapterCount} chapters, audio versions, PDF downloads, and RSS feed for $9.99/month.
              </p>
              <Link to="/subscribe" className="btn-primary inline-flex items-center gap-2" style={{ backgroundColor: series.accentColor }}>
                Become a Patron
                <ChevronRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      <section className="py-16 md:py-24 bg-ff-dark-section">
        <div className="max-w-5xl mx-auto px-4 md:px-6 text-center">
          <p className="label text-ff-text-muted mb-4">EXPLORE MORE</p>
          <h2 className="font-serif text-2xl md:text-3xl text-ff-text mb-8">Continue your journey</h2>
          <Link to="/library" data-testid="explore-library-btn" className="btn-secondary inline-flex items-center gap-2">
            Browse the Full Library
            <ChevronRight size={14} />
          </Link>
        </div>
      </section>

      {showSchedule && <ScheduleModal series={series} onClose={() => setShowSchedule(false)} />}
    </div>
  );
};

export default SeriesPage;
